import { useState } from "react";
import axios from "axios";
import { MapPin, Mail, Phone, Linkedin, Facebook, Send, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
    const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
    const [status, setStatus] = useState({ type: "", message: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus({ type: "", message: "" });
        try {
            const response = await axios.post(`${API}/contact`, formData);
            setStatus({
                type: "success",
                message: response.data.message || "Thank you! We'll be in touch soon.",
            });
            setFormData({ name: "", email: "", company: "", message: "" });
        } catch (error) {
            setStatus({
                type: "error",
                message: "Something went wrong. Please try again or email us directly.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact" className="relative py-32 border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    {/* Left */}
                    <div className="lg:col-span-5">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-accent-brand mb-4">
                            / Start a conversation
                        </div>
                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-display leading-[0.95] tracking-tight text-white mb-8">
                            Let&apos;s build
                            <br />
                            <em className="italic text-white/50">something</em>
                            <br />
                            that lasts.
                        </h2>
                        <p className="text-white/60 text-[15px] leading-relaxed mb-10 max-w-md">
                            Tell us about the outcome you want. We&apos;ll come back with a scope, timeline, and honest opinion — usually within 48 hours.
                        </p>

                        {/* Contact info */}
                        <div className="space-y-5">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
                                    <MapPin className="w-4 h-4 text-accent-brand" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Location</div>
                                    <div className="text-white text-sm">Chicago, IL · USA</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
                                    <Mail className="w-4 h-4 text-accent-brand" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Email</div>
                                    <a href="mailto:hr@apsdatatechnologies.com" className="text-white text-sm hover:text-accent-brand transition-colors">
                                        hr@apsdatatechnologies.com
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
                                    <Phone className="w-4 h-4 text-accent-brand" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Phone</div>
                                    <a href="tel:312-560-4654" className="text-white text-sm hover:text-accent-brand transition-colors">
                                        312-560-4654
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Social */}
                        <div className="mt-10 pt-8 border-t border-white/5 flex gap-3">
                            <a
                                href="https://www.linkedin.com/company/aps-data-technologies/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-lg border border-white/10 hover:bg-accent-brand hover:border-accent-brand hover:text-ink-950 text-white/60 flex items-center justify-center transition-all"
                            >
                                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                            <a
                                href="https://www.facebook.com/APSdatatechnologies/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-lg border border-white/10 hover:bg-accent-brand hover:border-accent-brand hover:text-ink-950 text-white/60 flex items-center justify-center transition-all"
                            >
                                <Facebook className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={handleSubmit}
                            className="rounded-2xl border border-white/10 bg-ink-950 p-8 md:p-10"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2 block">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg text-white placeholder-white/25 focus:outline-none focus:border-accent-brand transition-colors"
                                        placeholder="Your name"
                                        data-testid="contact-name-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2 block">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg text-white placeholder-white/25 focus:outline-none focus:border-accent-brand transition-colors"
                                        placeholder="your@email.com"
                                        data-testid="contact-email-input"
                                    />
                                </div>
                            </div>
                            <div className="mb-5">
                                <label className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2 block">
                                    Organization
                                </label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg text-white placeholder-white/25 focus:outline-none focus:border-accent-brand transition-colors"
                                    placeholder="University, district, city, or company"
                                    data-testid="contact-company-input"
                                />
                            </div>
                            <div className="mb-6">
                                <label className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2 block">
                                    Project / Outcome *
                                </label>
                                <textarea
                                    name="message"
                                    required
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows={5}
                                    className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg text-white placeholder-white/25 focus:outline-none focus:border-accent-brand transition-colors resize-none"
                                    placeholder="Tell us what you're trying to solve..."
                                    data-testid="contact-message-input"
                                />
                            </div>

                            {status.message && (
                                <div
                                    className={`mb-6 p-4 rounded-lg text-sm ${
                                        status.type === "success"
                                            ? "bg-accent-brand/10 border border-accent-brand/30 text-accent-brand"
                                            : "bg-red-500/10 border border-red-500/30 text-red-400"
                                    }`}
                                    data-testid="contact-status-message"
                                >
                                    {status.message}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="group w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-accent-brand text-ink-950 font-semibold hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="contact-submit-button"
                            >
                                {isSubmitting ? "Sending..." : "Send message"}
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 pt-10 border-t border-white/5">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <img
                            src="/logo.png"
                            alt="APS Data Technologies"
                            className="h-10 w-auto object-contain opacity-80"
                        />
                        <div className="text-white/40 text-sm">
                            © {new Date().getFullYear()} APS Data Technologies LLC.
                        </div>
                    </div>
                    <div className="text-white/40 text-[13px] font-mono">
                        Engineering intelligent software since 2011 · AI/ML since 2019.
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
